import { sendInitialPageView, setUserProperties } from '../analytics/trackingEvent';
import { getTransitionsNeedingProviderAttention } from '../transactions/transaction';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { digitalMediaIdStr } from '../util/digitalMedia';
import { isTooManyRequestsError, storableError } from '../util/errors';
import * as log from '../util/log';
import {
  getStoredLocale,
  localeFromPathname,
  normalizeLocale,
  resolveLocaleToPersist,
  setStoredLocale,
  shouldWriteProfileLocale,
} from '../util/locale';
import { LISTING_STATE_DRAFT } from '../util/types';

import { util as sdkUtil } from '../util/sdkLoader';
import { authInfo } from './auth.duck';
import {
  shouldReplaceStripeAccount,
  stripeAccountCreateSuccess,
} from './stripeConnectAccount.duck';
import { UserEvents } from './UserEvents';
import { startPractitionerOnboardingTransaction } from '../util/practitionerOnboardingTransaction';
import { ensureMessagingCredentialCaptured } from '../util/ensureMessagingCredential';
import { fetchCurrentUserHandleApi } from '../util/api';
import { applyProfileHandle } from '../util/profileHandle';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const UPDATE_USER_EVENTS_REQUEST = 'app/user/UPDATE_USER_EVENTS_REQUEST';
export const UPDATE_USER_EVENTS_SUCCESS = 'app/user/UPDATE_USER_EVENTS_SUCCESS';
export const UPDATE_USER_EVENTS_ERROR = 'app/user/UPDATE_USER_EVENTS_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  updateEventTrackerInProgress: false,
  updateEventTrackerError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPDATE_USER_EVENTS_REQUEST:
      return { ...state, updateEventTrackerInProgress: true };
    case UPDATE_USER_EVENTS_SUCCESS:
      return { ...state, updateEventTrackerInProgress: false };
    case UPDATE_USER_EVENTS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, updateEventTrackerInProgress: false, updateEventTrackerError: payload };
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return {
        ...state,
        currentUser: mergeCurrentUser(state.currentUser, payload),
      };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  const isStickyBannerError = err => !!err && !isTooManyRequestsError(err);
  return (
    isStickyBannerError(user.currentUserShowError) ||
    isStickyBannerError(user.currentUserHasListingsError) ||
    isStickyBannerError(user.currentUserNotificationCountError) ||
    isStickyBannerError(user.currentUserHasOrdersError)
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const updateUserEventsRequest = () => ({
  type: UPDATE_USER_EVENTS_REQUEST,
});

export const updateUserEventsSuccess = () => ({
  type: UPDATE_USER_EVENTS_SUCCESS,
});

export const updateUserEventsError = e => ({
  type: UPDATE_USER_EVENTS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    authorId: currentUser.id.uuid,
    page: 1,
    perPage: 1,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));
    })
    .catch(e => {
      // Soft-fail rate limits — do not sticky-banner "network error" on transient 429s.
      if (isTooManyRequestsError(e) || isTooManyRequestsError(storableError(e))) {
        return null;
      }
      return dispatch(fetchCurrentUserHasListingsError(storableError(e)));
    });
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => {
      if (isTooManyRequestsError(e) || isTooManyRequestsError(storableError(e))) {
        return null;
      }
      return dispatch(fetchCurrentUserHasOrdersError(storableError(e)));
    });
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  if (transitionsNeedingAttention.length === 0) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => {
      if (isTooManyRequestsError(e) || isTooManyRequestsError(storableError(e))) {
        return null;
      }
      return dispatch(fetchCurrentUserNotificationsError(storableError(e)));
    });
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));

    // Set GA4 user properties for guest and send initial page view
    setUserProperties({ isLoggedIn: false });
    sendInitialPageView();

    return Promise.resolve({});
  }

  const parameters = params || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store if present, but do not clobber a fully-fetched
      // account (with stripeAccountData) using the sparse currentUser include.
      if (currentUser.stripeAccount) {
        const existing = getState().stripeConnectAccount?.stripeAccount;
        if (shouldReplaceStripeAccount(existing, currentUser.stripeAccount)) {
          dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
        }
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));

      // Sync language preference. The URL wins so an `/ar/...` listing link
      // updates privateData.locale (emails) as well as the cookie.
      const profileLocaleRaw = normalizeLocale(
        currentUser?.attributes?.profile?.privateData?.locale
      );
      const storedLocaleRaw = getStoredLocale();
      const urlLocale =
        typeof window !== 'undefined' ? localeFromPathname(window.location.pathname) : null;
      const nextLocale = resolveLocaleToPersist({
        urlLocale,
        profileLocale: profileLocaleRaw,
        storedLocale: storedLocaleRaw,
      });

      if (nextLocale) {
        setStoredLocale(nextLocale);
      }
      if (
        shouldWriteProfileLocale({
          urlLocale,
          profileLocale: profileLocaleRaw,
          storedLocale: storedLocaleRaw,
        })
      ) {
        dispatch(updateCurrentUserLocale(nextLocale)).catch(() => {});
      }

      // Set GA4 user properties for logged-in user and send initial page view
      const userType = currentUser?.attributes?.profile?.publicData?.userType;
      setUserProperties({
        isLoggedIn: true,
        userType,
        userId: currentUser.id.uuid,
      });
      sendInitialPageView();

      if (typeof window === 'undefined') {
        return currentUser;
      }
      return fetchCurrentUserHandleApi()
        .then(result => {
          const withHandle = applyProfileHandle(currentUser, result?.data?.handle);
          if (withHandle !== currentUser) {
            dispatch(currentUserShowSuccess(withHandle));
          }
          return withHandle;
        })
        .catch(() => currentUser);
    })
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      dispatch(fetchCurrentUserNotifications());
      if (!currentUser.attributes.emailVerified) {
        dispatch(fetchCurrentUserHasOrders());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      // Keep provider messaging credential warm so welcome-message enable/send
      // does not depend on a single save click while the API happens to be up.
      ensureMessagingCredentialCaptured();
      // Callers (signupWithIdp, email verification, onboarding hooks) rely on the resolved user.
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
      return null;
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

/**
 *
 * @param {String} event
 * @return {*}
 */
export const updateUserEvents = event => {
  return (dispatch, getState, sdk) => {
    const { isAuthenticated } = getState().auth;
    const privateData = getState()?.user?.currentUser?.attributes?.profile?.privateData || {};

    if (!isAuthenticated) {
      return Promise.resolve({});
    }

    if (UserEvents[event] === undefined) {
      return Promise.resolve({});
    }

    // Events should be idempotent
    if (privateData[event]) {
      return Promise.resolve({});
    }

    dispatch(updateUserEventsRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    const payload = {
      privateData: {
        [event]: new Date().toISOString(),
      },
    };

    const handleError = e => {
      dispatch(updateUserEventsError(storableError(e)));
      log.error(e, 'update-user-events-failed', {
        event,
      });
      throw e;
    };

    const handleSuccess = response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const currentUser = entities[0];
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(updateUserEventsSuccess());
    };

    return sdk.currentUser
      .updateProfile(payload, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  };
};

/**
 * Change user type to practitioner
 * Updates the user's publicData.userType to 'practitioner'
 * @return {Promise}
 */
/**
 * Record that the current user opened a library media preview (for "New" badge dismissal).
 */
export const markLibraryMediaViewed = (listingId, mediaId) => {
  return (dispatch, getState, sdk) => {
    const { isAuthenticated } = getState().auth;
    if (!isAuthenticated || !listingId || mediaId == null) {
      return Promise.resolve({});
    }

    const listingIdStr = String(listingId);
    const mediaIdStr = digitalMediaIdStr(mediaId);
    const privateData = getState()?.user?.currentUser?.attributes?.profile?.privateData || {};
    const libraryMediaViewed = privateData.libraryMediaViewed || {};
    const listingViewed = libraryMediaViewed[listingIdStr] || {};

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    const payload = {
      privateData: {
        libraryMediaViewed: {
          ...libraryMediaViewed,
          [listingIdStr]: {
            ...listingViewed,
            [mediaIdStr]: new Date().toISOString(),
          },
        },
      },
    };

    return sdk.currentUser
      .updateProfile(payload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        dispatch(currentUserShowSuccess(entities[0]));
        return entities[0];
      })
      .catch(e => {
        log.error(e, 'mark-library-media-viewed-failed', {
          listingId: listingIdStr,
          mediaId: mediaIdStr,
        });
        throw e;
      });
  };
};

/**
 * Persist UI language preference on the current user (privateData.locale).
 * @param {'en'|'ar'} locale
 */
export const updateCurrentUserLocale = locale => {
  return (dispatch, getState, sdk) => {
    const { isAuthenticated } = getState().auth;
    const normalized = normalizeLocale(locale);
    if (!isAuthenticated || !normalized) {
      return Promise.resolve({});
    }

    const current = normalizeLocale(
      getState().user?.currentUser?.attributes?.profile?.privateData?.locale
    );
    if (current === normalized) {
      return Promise.resolve(getState().user.currentUser);
    }

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    const payload = {
      privateData: {
        locale: normalized,
      },
    };

    return sdk.currentUser
      .updateProfile(payload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        dispatch(currentUserShowSuccess(entities[0]));
        return entities[0];
      })
      .catch(e => {
        log.error(e, 'update-current-user-locale-failed', { locale: normalized });
        throw e;
      });
  };
};

export const changeUserTypeToPractitioner = () => {
  return (dispatch, getState, sdk) => {
    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    const payload = {
      publicData: {
        userType: 'practitioner',
      },
    };

    return sdk.currentUser
      .updateProfile(payload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update Redux store
        dispatch(currentUserShowSuccess(currentUser));

        // Start welcome + P3D reminder TX (idempotent; dashboard loadData also starts it)
        return startPractitionerOnboardingTransaction(sdk, currentUser).then(() =>
          dispatch(fetchCurrentUser()).then(user => user || currentUser)
        );
      })
      .catch(e => {
        log.error(e, 'change-user-type-failed');
        throw e;
      });
  };
};
